import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AuthLayout from '@/layouts/AuthLayout';
import { Field, OtpInput, inputClass } from '@/components/forms/form';
import { Button } from '@/components/ui';
import { useAuth } from '@/lib/auth';
import { OTP_LENGTH } from '@/lib/config';

const RESEND_SECONDS = 60;

/**
 * Companion (provider) sign-in — a separate route from the member door, so
 * nobody lands in the wrong dashboard. Same mechanism, different destination.
 */
export default function CompanionLogin() {
  const navigate = useNavigate();
  const { sendCode, verifyCode, session, loading } = useAuth();

  const [step, setStep] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email);

  useEffect(() => {
    if (!loading && session) navigate('/companion', { replace: true });
  }, [loading, session, navigate]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const submitEmail = async () => {
    if (!emailValid || busy) return;
    setBusy(true);
    setError('');
    try {
      await sendCode(email, { createUser: false });
      setStep('code');
      setCooldown(RESEND_SECONDS);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async () => {
    if (code.length < OTP_LENGTH || busy) return;
    setBusy(true);
    setError('');
    try {
      await verifyCode(email, code);
      navigate('/companion', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
      setCode('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout
      image="auth-companion.jpg"
      imageAlt="A woman on a terrace in morning light with a mug of chai"
      quote="Two hundred minutes last week, all from my own balcony."
      attribution="A companion, Hyderabad"
    >
      <span
        className="inline-block rounded-full px-3 py-1 text-xs font-semibold"
        style={{ background: 'var(--teal-soft)', color: 'var(--teal)' }}
      >
        Companion sign-in
      </span>

      {step === 'email' ? (
        <>
          <h1 className="mt-4 text-3xl font-semibold">Welcome back</h1>
          <p className="mt-2.5 text-[var(--ink-2)]">Sign in to go online and start taking calls.</p>

          <form
            className="mt-8 flex flex-col gap-5"
            onSubmit={(e) => {
              e.preventDefault();
              void submitEmail();
            }}
          >
            <Field
              id="email"
              label="Registered email"
              error={error}
              hint="The address you applied with."
            >
              <input
                id="email"
                type="email"
                autoComplete="email"
                autoFocus
                placeholder="you@example.com"
                className={inputClass}
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError('');
                }}
              />
            </Field>

            <Button size="lg" full type="submit" disabled={!emailValid || busy}>
              {busy ? 'Sending…' : 'Send code'}
            </Button>
          </form>

          <p className="mt-7 text-sm text-[var(--ink-2)]">
            Not a companion yet?{' '}
            <Link
              to="/companion/apply"
              className="font-medium text-[var(--marigold)] underline-offset-4 hover:underline"
            >
              Apply to join
            </Link>
          </p>

          <div className="mt-8 border-t border-[var(--line)] pt-6">
            <p className="text-sm text-[var(--muted)]">
              Looking to talk to someone instead?{' '}
              <Link
                to="/login"
                className="font-medium text-[var(--ink-2)] underline-offset-4 hover:text-[var(--ink)] hover:underline"
              >
                Member login
              </Link>
            </p>
          </div>
        </>
      ) : (
        <>
          <button
            type="button"
            onClick={() => {
              setStep('email');
              setCode('');
              setError('');
            }}
            className="mt-4 mb-6 block text-sm text-[var(--ink-2)] hover:text-[var(--ink)]"
          >
            ← Change email
          </button>

          <h1 className="text-3xl font-semibold">Enter the code</h1>
          <p className="mt-2.5 text-[var(--ink-2)]">
            Sent to <span className="font-medium text-[var(--ink)]">{email}</span>
          </p>

          <form
            className="mt-8 flex flex-col gap-5"
            onSubmit={(e) => {
              e.preventDefault();
              void submitCode();
            }}
          >
            <OtpInput value={code} onChange={setCode} />

            {error && (
              <p className="text-sm" style={{ color: 'var(--danger)' }}>
                {error}
              </p>
            )}

            <Button size="lg" full type="submit" disabled={code.length < OTP_LENGTH || busy}>
              {busy ? 'Checking…' : 'Verify and continue'}
            </Button>

            <p className="text-center text-sm text-[var(--muted)]">
              {cooldown > 0 ? (
                <>
                  Resend in {Math.floor(cooldown / 60)}:
                  {(cooldown % 60).toString().padStart(2, '0')}
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => void submitEmail()}
                  disabled={busy}
                  className="font-medium text-[var(--marigold)] underline-offset-4 hover:underline"
                >
                  Send another code
                </button>
              )}
            </p>
          </form>
        </>
      )}
    </AuthLayout>
  );
}
